import React from "react";
import type { DayPickerMultipleProps } from "react-day-picker";
import { DateFormat, formatDate } from "@remi/formatting";
import { RnChevronDown } from "@remi/icons/next";
import { isTruthy } from "@remi/ts-utils";

import { useMedia } from "../hooks";
import { useEffect } from "../hooks/useEffect";
import { cn } from "../lib/utils";
import type { OptionData, OptionSection } from "../types/option-data";
import { findSelectedOption } from "../utils";
import {
  DropdownModal,
  DropdownModalBody,
  DropdownModalContent,
  DropdownModalHeader,
  DropdownModalTrigger,
} from "./core/DropdownModal";
import { MultiSelectList } from "./core/MultiSelectList";
import { SelectList } from "./core/SelectList";
import type { RemiCalendarProps } from "./RemiCalendar";
import { RemiInput, type RemiInputProps } from "./RemiInput";
import { RemiPopover, RemiPopoverContent, RemiPopoverTrigger } from "./RemiPopover";
import { RemiSelectDate } from "./RemiSelectDate";

const SCROLL_PADDING = 24;

const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
  let element = node?.parentElement ?? null;
  while (element) {
    const style = getComputedStyle(element);
    if (/(auto|scroll)/.test(style.overflowY) && element.scrollHeight > element.clientHeight) {
      return element;
    }
    element = element.parentElement;
  }
  return null;
};

type DateSelectBaseProps = Omit<RemiInputProps, "onSelect" | "disabled"> & {
  carrot?: boolean;
  reset?: () => void;
  displayCount?: number;
  actions?: React.ReactNode;
  mode: "single" | "range" | "multiple";
  dateFormat?: DateFormat;
  dateFormatAdditionalProps?: {
    abbreviateDays?: boolean;
    abbreviateMonths?: boolean;
  };
  customDisplay?: string;
  position?: "top" | "bottom" | "left" | "right";
  disabledInput?: boolean;
  title?: string;
  mobileInline?: boolean;
  ref?: React.Ref<HTMLInputElement>;
};

/**
 * This needed to be separated because the discriminated union type for 'mode' is weird.
 * Used for the RemiSelectDateForm
 */
type DateSelectProps = RemiCalendarProps & DateSelectBaseProps;
export type DateSelectMultipleProps = DayPickerMultipleProps & DateSelectBaseProps;

const DateSelect = ({ ref, ...props }: DateSelectProps) => {
  const { isMobile } = useMedia();

  const [open, setOpen] = React.useState<boolean>(false);
  const inlineDateRef = React.useRef<HTMLDivElement | null>(null);

  const {
    dateFormat = DateFormat.DATE_MONTH,
    dateFormatAdditionalProps = {
      abbreviateDays: true,
      abbreviateMonths: true,
    },
    displayCount = 2,
    position = "bottom",
    toDate: _toDate,
    disabledInput,
    mobileInline,
    ...inputProps
  } = props;

  const { className, placeholder = "Select", carrot = true, icon, readOnly, title } = inputProps;

  let selectedLabel = "";
  if (props.selected) {
    if (props.mode === "single") {
      selectedLabel = formatDate(props.selected, {
        format: dateFormat,
        abbreviateDays: dateFormatAdditionalProps.abbreviateDays,
        abbreviateMonths: dateFormatAdditionalProps.abbreviateMonths,
      });
    } else if (props.mode === "range") {
      selectedLabel = `${formatDate(props.selected.from, { format: dateFormat })} - ${formatDate(props.selected.to, {
        format: dateFormat,
      })}`;
    } else if (props.selected.length > displayCount) {
      selectedLabel = `${props.selected.length} selected`;
    } else {
      selectedLabel = props.selected.map((date) => formatDate(date, { format: dateFormat })).join(", ");
    }
  }

  const isInlineDate = isMobile && mobileInline === true;

  useEffect(() => {
    if (!isInlineDate || !open) {
      return;
    }
    const handlePointerDown = (event: PointerEvent) => {
      const node = inlineDateRef.current;
      if (node && !event.composedPath().includes(node)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isInlineDate, open]);

  useEffect(() => {
    if (!isInlineDate || !open) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      const node = inlineDateRef.current;
      const scroller = getScrollParent(node);
      if (!node || !scroller) {
        return;
      }
      const overflowBelow =
        node.getBoundingClientRect().bottom + SCROLL_PADDING - scroller.getBoundingClientRect().bottom;
      if (overflowBelow > 0) {
        scroller.scrollTop += overflowBelow;
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [isInlineDate, open]);

  if (isInlineDate) {
    return (
      <div ref={inlineDateRef} className="w-full">
        <div
          role="button"
          aria-expanded={open}
          aria-label={typeof title === "string" ? title : placeholder}
          tabIndex={disabledInput !== true && readOnly !== true ? 0 : undefined}
          className={cn("w-full", disabledInput !== true && readOnly !== true && "cursor-pointer")}
          onClick={() => disabledInput !== true && readOnly !== true && setOpen(!open)}
          onKeyDown={(event) => {
            if (disabledInput === true || readOnly === true) {
              return;
            }
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setOpen(!open);
            }
          }}
        >
          <RemiSelectInput
            ref={ref}
            placeholder={placeholder}
            value={selectedLabel}
            open={open}
            setOpen={setOpen}
            carrot={carrot}
            icon={icon}
            className={cn("pointer-events-none", className)}
            {...inputProps}
            disabled={disabledInput}
            onSelect={() => {}}
          />
        </div>
        {open && (
          <div
            className="mt-2 flex justify-center rounded-lg border border-border-primary bg-background-primary py-2"
            onPointerDownCapture={() => {
              const scroller = getScrollParent(inlineDateRef.current);
              if (!scroller) {
                return;
              }
              const top = scroller.scrollTop;
              requestAnimationFrame(() => {
                if (scroller.scrollTop !== top) {
                  scroller.scrollTop = top;
                }
              });
            }}
          >
            <RemiSelectDate
              {...props}
              setOpen={setOpen}
              classNames={{
                ...props.classNames,
                weekdays: cn("justify-start gap-1", props.classNames?.weekdays),
                weekday: cn("w-10", props.classNames?.weekday),
                week: cn("justify-start gap-1", props.classNames?.week),
                day: cn("h-10 w-10", props.classNames?.day),
                day_button: cn("h-10 w-10 p-0", props.classNames?.day_button),
              }}
            />
          </div>
        )}
      </div>
    );
  }

  if (!isMobile) {
    return (
      <RemiPopover open={open} onOpenChange={(val) => disabledInput !== true && readOnly !== true && setOpen(val)}>
        <RemiPopoverTrigger asChild>
          <RemiSelectInput
            ref={ref}
            placeholder={placeholder}
            value={selectedLabel}
            open={open}
            setOpen={setOpen}
            carrot={carrot}
            icon={icon}
            className={cn(className)}
            {...inputProps}
            disabled={disabledInput}
            onSelect={() => {}}
          />
        </RemiPopoverTrigger>
        <RemiPopoverContent className={cn("w-fit p-0")} align="start" side={position}>
          <RemiSelectDate {...props} setOpen={setOpen} />
        </RemiPopoverContent>
      </RemiPopover>
    );
  }

  return (
    <DropdownModal open={open} onOpenChange={(val) => disabledInput !== true && setOpen(val)}>
      <DropdownModalTrigger asChild>
        <RemiSelectInput
          ref={ref}
          placeholder={placeholder}
          value={selectedLabel}
          open={open}
          setOpen={setOpen}
          carrot={carrot}
          icon={icon}
          className={cn(className)}
          {...inputProps}
          disabled={disabledInput}
          onSelect={() => {}}
        />
      </DropdownModalTrigger>
      <DropdownModalContent>
        <DropdownModalHeader>{title || "Select date"}</DropdownModalHeader>
        <DropdownModalBody>
          <RemiSelectDate
            {...props}
            className="w-full"
            classNames={{
              ...props.classNames,
              month: cn("w-full", props.classNames?.month),
              row: cn("flex justify-between", props.classNames?.row),
            }}
            setOpen={setOpen}
          />
        </DropdownModalBody>
      </DropdownModalContent>
    </DropdownModal>
  );
};

DateSelect.displayName = "DateSelect";

type SelectProps<T extends string> = Omit<RemiInputProps, "children"> & {
  search?: boolean;
  onSearch?: (element: HTMLInputElement) => void;
  selected: T | null;
  setSelected: (selected: T | null) => void;
  data: OptionData<T>[];
  sections?: readonly OptionSection<T>[];
  carrot?: boolean;
  hasReset?: boolean;
  actions?: React.ReactNode;
  canDeselect?: boolean;
  position?: "top" | "bottom" | "left" | "right";
  loading?: boolean;
  showAsText?: boolean;
  title?: string;
  /** Initial open state, read only at mount. Does not re-open the popover if changed after mount. */
  defaultOpen?: boolean;
  /** Controlled open state. When provided, the popover is fully controlled by the caller and ignores `defaultOpen`. Pair with `onOpenChange`. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  popoverContentClassName?: string;
  popoverAlign?: "start" | "center" | "end";
  mobileInline?: boolean;
  itemSize?: "sm" | "md" | "lg" | "auto";
  ref?: React.Ref<HTMLInputElement>;
};

type SelectInputProps = RemiInputProps & {
  open: boolean;
  setOpen: (open: boolean) => void;
  carrot: boolean;
};

const RemiSelectInput = ({
  ref,
  placeholder,
  value,
  open,
  setOpen,
  carrot,
  className,
  type,
  ...props
}: SelectInputProps) => {
  type = "text";
  return (
    <RemiInput
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpen(!open)}
      readOnly
      type={type}
      ref={ref}
      placeholder={placeholder}
      value={value}
      pseudoFocus={open}
      className={cn(className)}
      endIcon={carrot && <RnChevronDown className={cn("transition-all", open ? "rotate-180" : "rotate-0")} />}
      {...props}
    />
  );
};
RemiSelectInput.displayName = "RemiSelectInput";

function Select<T extends string>({
  placeholder = "Select",
  className,
  icon,
  carrot = true,
  search = false,
  onSearch,
  selected,
  setSelected,
  data,
  sections,
  hasReset = false,
  actions,
  canDeselect = false,
  position = "bottom",
  readOnly,
  loading = false,
  disabled,
  showAsText = false,
  title,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  popoverContentClassName,
  popoverAlign,
  mobileInline,
  itemSize: itemSizeProp,
  ...props
}: SelectProps<T>): React.JSX.Element {
  const [internalOpen, setInternalOpen] = React.useState<boolean>(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const setOpen = (val: boolean) => {
    if (!isControlled) {
      setInternalOpen(val);
    }
    onOpenChange?.(val);
  };

  const handleOpenChange = setOpen;
  const { isMobile } = useMedia();

  const flatData = React.useMemo(
    () => (sections === undefined ? data : sections.flatMap((section) => section.options)),
    [sections, data],
  );
  const selectedOption = findSelectedOption(flatData, selected);
  const selectedLabel: string = selectedOption ? selectedOption.label : "";

  const selectedIcon = selectedOption && typeof selectedOption === "object" && selectedOption.icon;

  const isDisabled = disabled === true || loading;
  const itemSize = itemSizeProp ?? (props.size === "xxs" ? ("sm" as const) : undefined);

  const handleInputClick = () => {
    if (!isDisabled && readOnly !== true) {
      setOpen(!open);
      props.onClick?.(new MouseEvent("click"));
    }
  };

  if (showAsText) {
    return <span>{selectedLabel}</span>;
  }

  if (!isMobile || mobileInline) {
    return (
      <RemiPopover open={open} onOpenChange={(val) => !isDisabled && readOnly !== true && handleOpenChange(val)}>
        <RemiPopoverTrigger asChild>
          <RemiSelectInput
            placeholder={placeholder}
            aria-label={typeof title === "string" ? title : placeholder}
            value={loading ? "Loading..." : selectedLabel}
            open={open}
            setOpen={setOpen}
            carrot={carrot}
            icon={isTruthy(selectedIcon) ? <span className="text-icon-primary">{selectedIcon}</span> : icon}
            onClick={handleInputClick}
            className={cn(className)}
            disabled={isDisabled}
            {...props}
          />
        </RemiPopoverTrigger>
        <RemiPopoverContent
          align={popoverAlign ?? "start"}
          side={position}
          className={cn("min-w-[var(--radix-popover-trigger-width)]", popoverContentClassName)}
        >
          <SelectList
            fill={true}
            setOpen={setOpen}
            data={data}
            sections={sections}
            search={search}
            onSearch={onSearch}
            selectedValue={selected}
            setSelectedValue={setSelected}
            hasReset={hasReset}
            actions={actions}
            canDeselect={canDeselect}
            itemSize={itemSize}
          />
        </RemiPopoverContent>
      </RemiPopover>
    );
  }

  return (
    <DropdownModal open={open} onOpenChange={(val) => !isDisabled && readOnly !== true && handleOpenChange(val)}>
      <DropdownModalTrigger asChild>
        <RemiSelectInput
          placeholder={placeholder}
          aria-label={typeof title === "string" ? title : placeholder}
          value={selectedLabel}
          open={open}
          setOpen={setOpen}
          carrot={carrot}
          icon={isTruthy(selectedIcon) ? <span className="text-icon-primary">{selectedIcon}</span> : icon}
          onClick={handleInputClick}
          className={cn(className)}
          disabled={isDisabled}
          {...props}
        />
      </DropdownModalTrigger>
      <DropdownModalContent ariaLabel={title === undefined ? placeholder : undefined}>
        {title && <DropdownModalHeader>{title}</DropdownModalHeader>}
        <DropdownModalBody>
          <SelectList
            setOpen={setOpen}
            data={data}
            sections={sections}
            search={search}
            onSearch={onSearch}
            selectedValue={selected}
            setSelectedValue={setSelected}
            hasReset={hasReset}
            actions={actions}
            canDeselect={canDeselect}
            itemSize={itemSize}
          />
        </DropdownModalBody>
      </DropdownModalContent>
    </DropdownModal>
  );
}

Select.displayName = "Select";

type MultiSelectProps<T extends string> = RemiInputProps & {
  search?: boolean;
  onSearch?: (element: HTMLInputElement) => void;
  selected: T[];
  setSelected: (selected: T[]) => void;
  data: readonly OptionData<T>[];
  carrot?: boolean;
  displayCount?: number;
  hasSelectAll?: boolean;
  hasReset?: boolean;
  actions?: React.ReactNode;
  position?: "top" | "bottom" | "left" | "right";
  title?: string;
};

function MultiSelect<T extends string>({
  placeholder = "Select",
  className,
  carrot = true,
  search = false,
  onSearch,
  selected,
  setSelected,
  displayCount = 2,
  hasSelectAll = false,
  hasReset = false,
  actions,
  data,
  readOnly,
  position = "bottom",
  title,
  ...props
}: MultiSelectProps<T>): React.JSX.Element {
  const [open, setOpen] = React.useState<boolean>(false);
  const selectedLabels = selected
    .map((value) => {
      const option = findSelectedOption(data, value);
      if (!option) {
        return null;
      }
      return typeof option === "string" ? option : option.label;
    })
    .filter((opt) => opt !== null);

  const selectedLabel: string =
    selectedLabels.length > displayCount ? `${selectedLabels.length} selected` : selectedLabels.join(", ");

  const handleInputClick = () => {
    if (props.disabled !== true && readOnly !== true) {
      setOpen(!open);
      props.onClick?.(new MouseEvent("click"));
    }
  };

  return (
    <RemiPopover open={open} onOpenChange={(val) => props.disabled !== true && readOnly !== true && setOpen(val)}>
      <RemiPopoverTrigger asChild>
        <RemiSelectInput
          placeholder={placeholder}
          aria-label={typeof title === "string" ? title : placeholder}
          value={selectedLabel}
          open={open}
          setOpen={setOpen}
          carrot={carrot}
          onClick={handleInputClick}
          className={cn(className)}
          {...props}
        />
      </RemiPopoverTrigger>
      <RemiPopoverContent side={position} align="start" className="min-w-[var(--radix-popover-trigger-width)]">
        <MultiSelectList
          fill={true}
          data={data}
          search={search}
          onSearch={onSearch}
          selectedValues={selected}
          setSelectedValues={setSelected}
          hasReset={hasReset}
          hasSelectAll={hasSelectAll}
          actions={actions}
        />
      </RemiPopoverContent>
    </RemiPopover>
  );
}
MultiSelect.displayName = "MultiSelect";

type RemiSelectProps<T extends string> =
  | (SelectProps<T> & { multiple?: false; date?: false })
  | (MultiSelectProps<T> & { multiple: true; date?: false })
  | (DateSelectProps & { multiple?: undefined; date: true });
function RemiSelect<T extends string>(props: RemiSelectProps<T>): React.JSX.Element {
  if (props.multiple === true) {
    return <MultiSelect {...props} />;
  }
  if (props.date === true) {
    return <DateSelect {...props} />;
  }
  return <Select {...props} />;
}

export type { DateSelectProps, MultiSelectProps, SelectProps };
export { DateSelect, MultiSelect, RemiSelect, Select };
